import { DelimitedGridCodec } from "@slot/presentation";
import type { GridCodec, GridModel } from "@slot/presentation";
import { GRID_SIZE } from "../gridSize";
import { BLANK, parseSymbolId } from "../rules/symbols";
import { BLANK_LIT, OUT_OF_PLAY } from "./SymbolCatalog";

/**
 * The server's board to the grid's model: the platform's strict codec (four cells a row, five rows) and every id one of
 * this game's (`rules/symbols.ts`). A coin's value is money: an id the game does not know, or a value that is not whole
 * minor units, refuses the frame instead of drawing a coin worth nothing. A blank in a row the frame does not show (a
 * GROW row while GROW is not in play) is a cell out of play: drawn as nothing, so no panel shows past the frame's edge;
 * a blank in an active line's row is lit (decision 56): the red badge.
 */
export class CoinGridCodec implements GridCodec {
  private readonly shape = new DelimitedGridCodec(GRID_SIZE.columns, GRID_SIZE.rows);
  private readonly rowsInPlay: () => number;
  private readonly isLit: (index: number) => boolean;

  public constructor(rowsInPlay: () => number, isLit: (index: number) => boolean) {
    this.rowsInPlay = rowsInPlay;
    this.isLit = isLit;
  }

  public decode(encodedGridSnapshot: string): GridModel {
    const model = this.shape.decode(encodedGridSnapshot);
    const rowsInPlay = this.rowsInPlay();
    const cells = model.cells.map((row, rowIndex) =>
      row.map((cell, colIndex) => {
        if (!parseSymbolId(cell.symbolId)) throw new Error(`CoinGridCodec: "${cell.symbolId}" is not a symbol of this game (board ${encodedGridSnapshot})`);
        if (cell.symbolId !== BLANK) return cell;
        if (rowIndex >= rowsInPlay) return { ...cell, symbolId: OUT_OF_PLAY };
        return this.isLit(rowIndex * GRID_SIZE.columns + colIndex) ? { ...cell, symbolId: BLANK_LIT } : cell;
      }),
    );
    return { ...model, cells };
  }
}
